// Photo handling component and utilities
import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Grid,
  Button
} from '@mui/material';
import {
  CloudUpload as Upload,
  Close as X,
  PhotoCamera as Camera
} from '@mui/icons-material';

export const PhotoUploadHandler = {
  // Handle file selection
  handleFileSelect: (e, formData, setFieldValue) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      url: URL.createObjectURL(file),
      name: file.name
    }));
    const currentPhotos = formData.photos || [];
    setFieldValue('photos', [...currentPhotos, ...newPhotos]);
  },

  // Remove photo
  removePhoto: (id, formData, setFieldValue) => {
    const currentPhotos = formData.photos || [];
    const updatedPhotos = currentPhotos.filter(photo => photo.id !== id);
    setFieldValue('photos', updatedPhotos);
  }
};

// Photo Documentation Section Component
export const PhotoDocumentationSection = ({ 
  formData, 
  fileInputRef, 
  handleFileSelect, 
  removePhoto, 
  uploading 
}) => {
  return (
    <Card elevation={2} sx={{ borderRadius: 2 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 2,
        borderBottom: 1,
        borderColor: 'grey.200',
        bgcolor: 'grey.50'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Camera sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold">
            Photo Documentation
          </Typography>
          {formData.photos && formData.photos.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              ({formData.photos.length} photo{formData.photos.length !== 1 ? 's' : ''})
            </Typography>
          )}
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Upload />}
          onClick={() => fileInputRef.current?.click()}
          sx={{ minWidth: 'auto' }}
        >
          Add Photos
        </Button>
      </Box>

      <CardContent sx={{ p: 2 }}>
        {/* Show upload area only if no photos */}
        {(!formData.photos || formData.photos.length === 0) && (
          <Box
            sx={{
              border: 2,
              borderStyle: 'dashed',
              borderColor: 'grey.300',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              bgcolor: 'grey.50',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'primary.50'
              }
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
            <Typography variant="body1" color="text.primary" sx={{ mb: 0.5 }}>
              Upload Photos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click to browse or drag and drop images
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              JPG, PNG, WebP (Max 5MB each)
            </Typography>
          </Box>
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {/* Compact Photo Grid */}
        {formData.photos && formData.photos.length > 0 && (
          <Grid container spacing={1.5}>
            {formData.photos.map((photo) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={photo.id}>
                <Card 
                  sx={{ 
                    position: 'relative', 
                    border: 1, 
                    borderColor: 'grey.200',
                    borderRadius: 1,
                    overflow: 'hidden',
                    '&:hover .delete-btn': {
                      opacity: 1
                    }
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <img
                      src={photo.url}
                      alt={photo.name}
                      style={{
                        width: '100%',
                        height: 120,
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                    <IconButton
                      className="delete-btn"
                      size="small"
                      onClick={() => removePhoto(photo.id)}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        bgcolor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        '&:hover': { 
                          bgcolor: 'error.main',
                          opacity: 1
                        }
                      }}
                    >
                      <X fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ p: 1, bgcolor: 'grey.50' }}>
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      noWrap
                      sx={{ fontSize: '0.7rem' }}
                    >
                      {photo.name}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="primary.main">
              Uploading photos...
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};


